import React, { useEffect, useRef, useState } from "react";
import "./MULTISEARCHABLE.css";

export interface Option {
  value: string;
  label: string;
}

interface SearchableSelectProps {
  label: string;
  name: string;
  value: string[];
  options: Option[];
  onChange: (name: string, values: string[], options: Option[]) => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  loading?: boolean;
  onInputChange?: (value: string) => void;
  hasRowGrid?: boolean;
  onOpen?: () => void; // ADD THIS
}

const MultiSearchableSelect: React.FC<SearchableSelectProps> = ({
  label,
  name,
  value,
  options,
  onChange,
  placeholder = "Select",
  required,
  disabled = false,
  error,
  loading,
  onInputChange,
  hasRowGrid = false,
  onOpen,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const optionRefs = useRef<(HTMLDivElement | null)[]>([]);

  const selectedOptions = options.filter((opt) => value.includes(opt.value));

  const filteredOptions = options?.filter((opt) =>
    opt.label.toLowerCase().includes(search.toLowerCase()),
  );

  const allFilteredSelected =
    filteredOptions.length > 0 &&
    filteredOptions.every((opt) => value.includes(opt.value));

  const handleSelectAll = () => {
    const filteredValues = filteredOptions.map((opt) => opt.value);

    let updatedValues: string[];

    if (allFilteredSelected) {
      // Deselect filtered options
      updatedValues = value.filter(
        (selectedValue) => !filteredValues.includes(selectedValue),
      );
    } else {
      // Select filtered options
      updatedValues = Array.from(new Set([...value, ...filteredValues]));
    }

    const updatedOptions = options.filter((opt) =>
      updatedValues.includes(opt.value),
    );

    onChange(name, updatedValues, updatedOptions);
  };
  /** Close dropdown on outside click */
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen) {
      setHighlightedIndex(0);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && optionRefs.current[highlightedIndex]) {
      optionRefs.current[highlightedIndex]?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [highlightedIndex, isOpen]);

  const handleSelect = (option: Option) => {
    let updatedValues: string[];

    if (value?.includes(option.value)) {
      updatedValues = value.filter((v) => v !== option.value);
    } else {
      updatedValues = [...value, option.value];
    }

    const updatedOptions = options.filter((opt) =>
      updatedValues.includes(opt.value),
    );

    onChange(name, updatedValues, updatedOptions);
  };

  return (
    <div
      className="position-relative w-100"
      ref={containerRef}
      data-field-name={name}
    >
      <div className={hasRowGrid ? "row align-items-center" : ""}>
        <label
          className={`form-label formfield-label ${hasRowGrid ? "col-auto mb-0" : "w-100"}`}
        >
          {label} {required && <span className="text-danger fs-16">*</span>}
        </label>

        {/* Select Box Wrapper */}
        <div
          className={hasRowGrid ? "col-8 col-md-4 position-relative px-0" : ""}
        >
          <div className="position-relative">
            <div
            tabIndex={disabled ? -1 : 0}
              className={`msearch-trigger formfield-control d-flex align-items-center justify-content-between ${
                error ? "is-invalid" : ""
              } ${disabled ? "msearch-trigger-disabled" : ""} ${
                isOpen ? "msearch-trigger-open" : ""
              }`}
              onClick={() => {
                if (disabled) return;

                if (!isOpen) {
                  onOpen?.();
                }

                setIsOpen((prev) => !prev);
              }}
            >
              <span
                className={`msearch-trigger-text ${
                  selectedOptions.length === 0 ? "text-muted" : ""
                }`}
              >
                {selectedOptions.length > 0
                  ? `${selectedOptions.length} selected`
                  : placeholder}
              </span>
              <svg
                className={`msearch-chevron ${isOpen ? "msearch-chevron-open" : ""}`}
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M2.5 4.5L6 8L9.5 4.5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>

            {/* selected labels */}
            {value.length > 0 && (
              <div className="small mt-1 px-1 text-muted mb-1 msearch-selected-labels">
                {selectedOptions?.map((opt) => opt.label).join(", ")}
              </div>
            )}

            {error && (
              <div className="text-danger small mt-1 ms-1">{error}</div>
            )}

            {/* Dropdown */}
            {isOpen && (
              <div className="msearch-dropdown border rounded shadow-sm bg-white position-absolute w-100 z-3">
                {/* Search Input */}
                <div className="p-2 border-bottom msearch-search-wrapper">
                  <svg
                    className="msearch-search-icon"
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle
                      cx="6"
                      cy="6"
                      r="4.5"
                      stroke="currentColor"
                      strokeWidth="1.3"
                    />
                    <path
                      d="M9.5 9.5L13 13"
                      stroke="currentColor"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                    />
                  </svg>
                  <input
                    type="text"
                    className="form-control formfield-control msearch-search-input"
                    placeholder="Search..."
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setHighlightedIndex(0);
                      onInputChange?.(e.target.value);
                    }}
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === "ArrowDown") {
                        e.preventDefault();
                        setHighlightedIndex((prev) =>
                          prev < filteredOptions.length - 1 ? prev + 1 : 0,
                        );
                      } else if (e.key === "ArrowUp") {
                        e.preventDefault();
                        setHighlightedIndex((prev) =>
                          prev > 0 ? prev - 1 : filteredOptions.length - 1,
                        );
                      } else if (e.key === "Enter") {
                        e.preventDefault();
                        if (filteredOptions[highlightedIndex]) {
                          handleSelect(filteredOptions[highlightedIndex]);
                        }
                      } else if (e.key === "Escape") {
                        setIsOpen(false);
                        setSearch("");
                      }
                    }}
                  />
                </div>

                {/* Options */}
                {/* <div className="msearch-options py-1">
                      {loading ? (
                        <div className="px-3 py-2 text-muted small">
                          Loading...
                        </div>
                      ) : filteredOptions.length > 0 ? (
                        filteredOptions.map((opt, index) => (
                          <div
                            key={opt.value}
                            ref={(el) => {
                              optionRefs.current[index] = el;
                            }}
                            className={`msearch-option px-3 py-2 d-flex align-items-center gap-2 ${
                              index === highlightedIndex
                                ? "dropdownHeighlightColor msearch-option-highlighted"
                                : ""
                            } ${value.includes(opt.value) ? "msearch-option-selected" : ""}`}
                            onMouseEnter={() => setHighlightedIndex(index)}
                            onClick={() => handleSelect(opt)}
                          >
                            <input
                              type="checkbox"
                              checked={value.includes(opt.value)}
                              onChange={() => {}}
                              className="msearch-checkbox"
                            />
                            <span className="msearch-option-label">
                              {opt.label}
                            </span>
                          </div>
                        ))
                      ) : (
                        <div className="px-3 py-2 text-muted small">
                          No results found
                        </div>
                      )}
                    </div> */}
                {/* Options */}
                <div className="msearch-options py-1">
                  {loading ? (
                    <div className="px-3 py-2 text-muted small">Loading...</div>
                  ) : (
                    <>
                      {/* Select All */}
                      {filteredOptions.length > 0 && (
                        <div
                          className="msearch-select-all px-3 py-2 d-flex align-items-center gap-2 border-bottom"
                          onClick={handleSelectAll}
                        >
                          <input
                            type="checkbox"
                            checked={allFilteredSelected}
                            onChange={() => {}}
                            className="msearch-checkbox"
                          />

                          <span className="msearch-option-label fw-semibold">
                            {allFilteredSelected
                              ? "Deselect All"
                              : "Select All"}
                          </span>
                        </div>
                      )}

                      {/* Individual Options */}
                      {filteredOptions.length > 0 ? (
                        filteredOptions.map((opt, index) => (
                          <div
                            key={opt.value}
                            ref={(el) => {
                              optionRefs.current[index] = el;
                            }}
                            className={`msearch-option px-3 py-2 d-flex align-items-center gap-2 ${
                              index === highlightedIndex
                                ? "dropdownHeighlightColor msearch-option-highlighted"
                                : ""
                            } ${
                              value.includes(opt.value)
                                ? "msearch-option-selected"
                                : ""
                            }`}
                            onMouseEnter={() => setHighlightedIndex(index)}
                            onClick={() => handleSelect(opt)}
                          >
                            <input
                              type="checkbox"
                              checked={value.includes(opt.value)}
                              onChange={() => {}}
                              className="msearch-checkbox"
                            />

                            <span className="msearch-option-label">
                              {opt.label}
                            </span>
                          </div>
                        ))
                      ) : (
                        <div className="px-3 py-2 text-muted small">
                          No results found
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MultiSearchableSelect;

// import React, { useEffect, useRef, useState } from "react";

// export interface Option {
//   value: string;
//   label: string;
// }

// interface SearchableSelectProps {
//   label: string;
//   name: string;
//   value: string;
//   options: Option[];
//   // onChange: (name: string, values: string[], options: Option[]) => void;
//   onChange: (name: string, value: string, option: Option) => void;
//   placeholder?: string;
//   required?: boolean;
//   disabled?: boolean;
//   error?: string;
//   loading?: boolean;
//   onInputChange?: (value: string) => void;
//   hasRowGrid?: boolean;
//   onOpen?: () => void;
// }

// const MultiSearchableSelect: React.FC<SearchableSelectProps> = ({
//   label,
//   name,
//   value,
//   options,
//   onChange,
//   placeholder = "-- Select --",
//   required,
//   disabled = false,
//   error,
//   loading,
//   onInputChange,
//   hasRowGrid = false,
//   onOpen,
// }) => {
//   const [isOpen, setIsOpen] = useState(false);
//   const [search, setSearch] = useState("");
//   const [highlightedIndex, setHighlightedIndex] = useState(0);
//   const containerRef = useRef<HTMLDivElement | null>(null);
//   const optionRefs = useRef<(HTMLDivElement | null)[]>([]);

//   // const selectedOptions = options.filter((opt) => value?.includes(opt.value));
//   const selectedOption = options.find((opt) => opt.value === value);

//   const filteredOptions = options?.filter((opt) =>
//     opt.label.toLowerCase()?.includes(search.toLowerCase()),
//   );

//   /** Close dropdown on outside click */
//   useEffect(() => {
//     const handleClickOutside = (e: MouseEvent) => {
//       if (
//         containerRef.current &&
//         !containerRef.current.contains(e.target as Node)
//       ) {
//         setIsOpen(false);
//         setSearch("");
//       }
//     };
//     document.addEventListener("mousedown", handleClickOutside);
//     return () => document.removeEventListener("mousedown", handleClickOutside);
//   }, []);

//   useEffect(() => {
//     if (isOpen) {
//       setHighlightedIndex(0);
//     }
//   }, [isOpen]);

//   useEffect(() => {
//     if (isOpen && optionRefs.current[highlightedIndex]) {
//       optionRefs.current[highlightedIndex]?.scrollIntoView({
//         behavior: "smooth",
//         block: "nearest",
//       });
//     }
//   }, [highlightedIndex, isOpen]);

//   // handleSelect
//   const handleSelect = (option: Option) => {
//     // let updatedValues: string[];
//     // console.log("name:", name, "value:", value);
//     // console.log("options:", option);

//     // if (value?.includes(option.value)) {
//     //   updatedValues = value.filter((v) => v !== option.value);
//     // } else {
//     //   updatedValues = [...value, option.value];
//     // }

//     // const updatedOptions = options.filter((opt) =>
//     //   updatedValues?.includes(opt.value),
//     // );

//     // onChange(name, updatedValues, updatedOptions);
//     onChange(name, option.value, option);
//     setIsOpen(false); // close after selection
//     setSearch("");
//   };

//   return (
//     <div className="position-relative w-100 " ref={containerRef}>
//       <div className={hasRowGrid ? "row align-items-center" : ""}>
//         <label
//           className={`form-label ${hasRowGrid ? "col-auto mb-0 fw-semibold" : "w-100"}`}
//         >
//           {label} {required && <sup className="text-danger">*</sup>}
//         </label>

//         {/* Select Box Wrapper */}
//         <div
//           // style={{ minWidth: "110px" }}
//           // style={{ minWidth: "190px", maxWidth: "250px" }}
//           className={hasRowGrid ? "col-8 col-md-4 position-relative px-0" : ""}
//         >
//           <div
//             className="form-select border border d-flex align-items-center"
//             style={{
//               minHeight: "42px",
//               cursor: disabled ? "not-allowed" : "pointer",
//               overflow: "hidden",
//               whiteSpace: "nowrap",
//               textOverflow: "ellipsis",
//             }}
//             onClick={() => {
//               if (!disabled) {
//                 setIsOpen((prev) => {
//                   const next = !prev;

//                   if (next) {
//                     setTimeout(() => {
//                       onOpen?.(); //  SAFE now
//                     }, 0);
//                   }

//                   return next;
//                 });
//               }
//             }}
//           >
//             -- Select --
//             {/* {selectedOptions?.length > 0 &&
//               selectedOptions[selectedOptions.length - 1]?.label} */}
//           </div>

//           {/* selected labels */}
//           {/* {value?.length > 0 && (
//             <div
//               className={`small mt-1 pt-1 px-1 text-muted mb-1 ${selectedOptions?.length > 0 ? "" : "text-muted"}`}
//             >
//               {selectedOptions?.map((opt) => opt.label).join(", ")}

//             </div>
//           )} */}

//           {value && selectedOption && (
//             <div className="small mt-1 pt-1 px-1 text-muted mb-1">
//               {selectedOption.label}
//             </div>
//           )}
//           {/* /selected labels */}

//           {error && <div className="text-danger small mt-1 ms-1">{error}</div>}

//           {/* Dropdown */}
//           {isOpen && (
//             <div
//               className="border rounded mt-1 shadow-sm bg-white position-absolute w-100 z-3 "
//               style={{ top: "72px" }}
//             >
//               {/* Search Input */}
//               <div className="p-2 border-bottom">
//                 <input
//                   type="text"
//                   className="form-control"
//                   placeholder="Search..."
//                   value={search}
//                   onChange={(e) => {
//                     setSearch(e.target.value);
//                     setHighlightedIndex(0);
//                     onInputChange?.(e.target.value);
//                   }}
//                   autoFocus
//                   onKeyDown={(e) => {
//                     if (e.key === "ArrowDown") {
//                       e.preventDefault();
//                       setHighlightedIndex((prev) =>
//                         prev < filteredOptions?.length - 1 ? prev + 1 : 0,
//                       );
//                     } else if (e.key === "ArrowUp") {
//                       e.preventDefault();
//                       setHighlightedIndex((prev) =>
//                         prev > 0 ? prev - 1 : filteredOptions?.length - 1,
//                       );
//                     } else if (e.key === "Enter") {
//                       e.preventDefault();
//                       if (filteredOptions[highlightedIndex]) {
//                         handleSelect(filteredOptions[highlightedIndex]);
//                       }
//                     } else if (e.key === "Escape") {
//                       setIsOpen(false);
//                       setSearch("");
//                     }
//                   }}
//                 />
//               </div>

//               {/* Options */}
//               <div
//                 className="py-2"
//                 style={{ maxHeight: "160px", overflowY: "auto", zIndex: 99 }}
//               >
//                 {loading ? (
//                   <div className="px-3 py-2 text-muted">Loading...</div>
//                 ) : filteredOptions?.length > 0 ? (
//                   filteredOptions?.map((opt, index) => (
//                     <div
//                       key={opt.value}
//                       ref={(el) => {
//                         optionRefs.current[index] = el;
//                       }}
//                       style={{ cursor: "pointer" }}
//                       className={`px-2 3 py-1 d-flex align-items-start gap-2 ${
//                         index === highlightedIndex
//                           ? "dropdownHeighlightColor"
//                           : ""
//                       }`}
//                       onMouseEnter={() => setHighlightedIndex(index)}
//                       onClick={() => handleSelect(opt)}
//                     >
//                       {/* <input
//                         type="checkbox"
//                         checked={value?.includes(opt.value)}
//                         className="mt-1"
//                       /> */}

//                       <span>{opt.label}</span>
//                     </div>
//                   ))
//                 ) : (
//                   <div className="px-3 py-2 text-muted">No results found</div>
//                 )}
//               </div>
//             </div>
//           )}
//         </div>
//       </div>
//     </div>
//   );
// };

// export default MultiSearchableSelect;
